import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Bot, Check, X, ExternalLink, AlertTriangle, Clock } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { TOOL_LABEL, STATUS_META, EDITABLE_FIELDS, executeApproved } from '../../lib/agentActions';
import NewInvoiceDialog from '../../components/admin/NewInvoiceDialog';

// The human checkpoint for the assistant connector. The assistant proposed
// something; the person who asked reads it, corrects it, and approves or
// denies. Only then does this page perform the write, through the ordinary
// endpoints and under this user's own session.

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-[13px] placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30';
const labelCls = 'block text-[11px] font-medium text-gray-400/80 mb-1.5 tracking-wide';

function minutesLeft(iso) {
  const ms = new Date(String(iso).replace(' ', 'T') + (String(iso).endsWith('Z') ? '' : 'Z')).getTime() - Date.now();
  return Math.max(0, Math.round(ms / 60000));
}

export default function AssistantAction() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null); // { ok, result?, error? }
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // update_invoice: the raw invoice + line items for the editor

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/admin/agent-actions/${id}`);
      setData(res);
      setForm(res.action.payload || {});
    } catch (err) { setError(err?.message || 'Could not load this proposal'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const action = data?.action;
  const fields = action ? EDITABLE_FIELDS[action.tool] || [] : [];
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const finish = async (ok, result, err) => {
    try { const res = await api.post(`/api/admin/agent-actions/${id}/complete`, { ok, result: result || null, error: err || null }); setData((d) => ({ ...d, action: res.action })); }
    catch { /* the outcome is shown regardless */ }
    setOutcome({ ok, result, error: err });
  };

  const approve = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const payload = { ...form };
      for (const f of fields) if (f.type === 'number') payload[f.key] = payload[f.key] === '' || payload[f.key] == null ? null : Number(payload[f.key]);
      if (action.tool === 'log_interaction') payload.followUpNeeded = !!payload.followUpAt;
      const res = await api.post(`/api/admin/agent-actions/${id}/decide`, { decision: 'approved', payload });
      setData((d) => ({ ...d, action: res.action }));
      if (action.tool === 'draft_invoice') { setInvoiceOpen(true); return; }
      if (action.tool === 'update_invoice') {
        try {
          const inv = await api.get(`/api/admin/financial/invoices/${action.payload.invoiceId}`);
          setEditTarget({ ...(inv.raw || inv.invoice), lineItems: inv.line_items || [] });
          setInvoiceOpen(true);
        } catch (err) { await finish(false, null, err instanceof ApiError ? err.message : 'Could not load the invoice'); }
        return;
      }
      try {
        const result = await executeApproved(res.action);
        await finish(true, result, null);
      } catch (err) {
        await finish(false, null, err instanceof ApiError ? err.message : (err?.message || 'The write was refused'));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record the decision');
    } finally { setBusy(false); }
  };

  const deny = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const res = await api.post(`/api/admin/agent-actions/${id}/decide`, { decision: 'denied', note: note.trim() || null });
      setData((d) => ({ ...d, action: res.action }));
    } catch (err) { setError(err instanceof ApiError ? err.message : 'Could not record the decision'); }
    finally { setBusy(false); }
  };

  if (error && !data) return <div className="max-w-2xl mx-auto text-[13px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-4" role="alert">{error}</div>;
  if (!action) return <div className="max-w-2xl mx-auto h-24 rounded-xl bg-white/[0.03] animate-pulse" />;

  const st = STATUS_META[action.status] || STATUS_META.pending;
  const pending = action.status === 'pending';
  const mine = data.canDecide;
  const left = minutesLeft(action.expiresAt);

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/admin/assistant" className="text-[12px] text-gray-500 hover:text-white">← Assistant</Link>
      <div className="mt-2 mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500"><Bot className="w-3.5 h-3.5" aria-hidden="true" /> {action.clientName || 'Your assistant'} proposes</div>
          <h1 className="text-xl font-semibold text-white tracking-tight mt-1">{TOOL_LABEL[action.tool] || action.tool}</h1>
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] border ${st.tone}`} data-testid="action-status">{st.label}</span>
      </div>

      <section className="rounded-2xl border border-[#B0FC38]/25 bg-[#B0FC38]/[0.04] p-4 mb-4" data-testid="action-summary">
        <p className="text-[14px] text-white leading-relaxed">{action.summary}</p>
        <div className="mt-2 flex items-center gap-3 flex-wrap text-[11.5px] text-gray-500">
          {action.accountId && <Link to={`/admin/accounts/${action.accountId}`} className="inline-flex items-center gap-1 text-gray-300 hover:text-white">{action.accountName || 'Open the school'} <ExternalLink className="w-3 h-3" aria-hidden="true" /></Link>}
          {pending && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" aria-hidden="true" /> {left > 0 ? `Expires in ${left} min` : 'Expiring'}</span>}
          <span>Asked by {action.userName || 'you'}</span>
        </div>
      </section>

      {!mine && pending && <div role="note" className="mb-4 text-[12.5px] text-amber-200 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">Only {action.userName || 'the person who asked'} can approve or deny this proposal.</div>}

      {pending && mine && (
        <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 mb-4" data-testid="action-form">
          <h2 className="text-[13px] font-medium text-white mb-3">Check the details. Correct anything the assistant got wrong.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map((f) => (
              <label key={f.key} className={`block ${f.type === 'textarea' ? 'sm:col-span-2' : ''}`}>
                <span className={labelCls}>{f.label}{f.required ? ' *' : ''}</span>
                {f.type === 'select' ? (
                  <select value={form[f.key] ?? ''} onChange={set(f.key)} className={inputCls} aria-label={f.label}>{f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                ) : f.type === 'textarea' ? (
                  <textarea rows={3} value={form[f.key] ?? ''} onChange={set(f.key)} className={`${inputCls} resize-y`} aria-label={f.label} />
                ) : f.type === 'checkbox' ? (
                  <input type="checkbox" checked={!!form[f.key]} onChange={set(f.key)} className="accent-[#B0FC38] mt-2" aria-label={f.label} />
                ) : (
                  <input type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'} value={form[f.key] ?? ''} onChange={set(f.key)} placeholder={f.placeholder || ''} className={inputCls} aria-label={f.label} />
                )}
                {f.hint && <span className="block text-[11px] text-gray-500 mt-1">{f.hint}</span>}
              </label>
            ))}
          </div>
          {(action.tool === 'draft_reply' || action.tool === 'ask_submitter' || action.tool === 'overdue_reminder') && (
            <div className="text-[12.5px] text-amber-100 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-3 mb-3" data-testid="school-facing">
              <b>This emails the school.</b>{' '}
              {action.tool === 'draft_reply' && <>Reply on “{action.payload?.subject || 'the thread'}” from {action.payload?.fromEmail} to {(action.payload?.to || []).join(', ')}.</>}
              {action.tool === 'ask_submitter' && <>Goes to {action.payload?.submitterName || 'the submitter'} from NexSav Support{action.payload?.awaitReply !== false ? ' and marks the item Awaiting reply' : ''}.</>}
              {action.tool === 'overdue_reminder' && <>Overdue reminder for invoice {action.payload?.invoiceNumber} (${Number(action.payload?.amountDollars || 0).toLocaleString('en-US')}).</>}
              {' '}Read the wording before you approve.
            </div>
          )}
          {action.tool === 'update_invoice' && (
            <div className="text-[12.5px] text-gray-300 rounded-lg border border-white/[0.06] bg-black/30 p-3" data-testid="invoice-change">
              <div>Invoice <b>{action.payload?.invoiceNumber}</b> is currently ${Number(action.payload?.current?.amountDollars || 0).toLocaleString('en-US')} ({action.payload?.current?.status}).</div>
              <ul className="mt-1 list-disc pl-5 text-gray-400">
                {action.payload?.changes?.amountDollars != null && <li>Total → ${Number(action.payload.changes.amountDollars).toLocaleString('en-US')}</li>}
                {action.payload?.changes?.description && <li>Line description → “{action.payload.changes.description}”</li>}
                {action.payload?.changes?.validThrough && <li>Valid through → {action.payload.changes.validThrough}</li>}
                {action.payload?.changes?.notesClient != null && <li>Note to the school → “{action.payload.changes.notesClient}”</li>}
              </ul>
              {action.payload?.reason && <div className="mt-1 text-gray-500">Reason given: {action.payload.reason}</div>}
              <div className="mt-1 text-gray-500">Approve opens the invoice editor with the current line items. Make the change there and save; that is what the assistant is told about.</div>
            </div>
          )}
          {action.tool === 'move_stage' && action.payload?.structured_payload && Object.keys(action.payload.structured_payload).length > 0 && (
            <dl className="mt-3 text-[12px] text-gray-400 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              {Object.entries(action.payload.structured_payload).filter(([k]) => k !== 'summary').map(([k, v]) => <React.Fragment key={k}><dt className="text-gray-500">{k.replace(/_/g, ' ')}</dt><dd className="text-gray-200 m-0">{String(v)}</dd></React.Fragment>)}
            </dl>
          )}
          <div className="mt-4">
            <label className="block"><span className={labelCls}>Note back to the assistant (optional, sent when you deny)</span>
              <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Wrong school, it was the one in Queens" className={inputCls} aria-label="Note" /></label>
          </div>
          {error && <div role="alert" className="mt-3 text-[12px] text-red-300">{error}</div>}
          <div className="mt-4 flex items-center justify-end gap-2 flex-wrap">
            <button type="button" onClick={deny} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/[0.12] text-[13px] text-gray-300 hover:text-red-300 hover:border-red-500/30 disabled:opacity-50" data-testid="action-deny"><X className="w-3.5 h-3.5" aria-hidden="true" /> Deny</button>
            <button type="button" onClick={approve} disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-semibold hover:bg-[#9BE22E] disabled:opacity-50" data-testid="action-approve"><Check className="w-3.5 h-3.5" aria-hidden="true" /> {busy ? 'Working…' : action.tool === 'draft_invoice' ? 'Approve and open the draft' : action.tool === 'update_invoice' ? 'Approve and open the editor' : 'Approve and do it'}</button>
          </div>
        </section>
      )}

      {outcome && (
        <section role="status" className={`rounded-2xl border p-4 mb-4 ${outcome.ok ? 'border-emerald-500/25 bg-emerald-500/[0.06]' : 'border-red-500/25 bg-red-500/[0.06]'}`} data-testid="action-outcome">
          {outcome.ok ? (
            <>
              <div className="text-[14px] text-emerald-200 font-medium">Done. The assistant will see this as executed.</div>
              {outcome.result?.warning && <div className="mt-1 text-[12px] text-amber-200 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> {outcome.result.warning}</div>}
              {outcome.result?.link && <Link to={outcome.result.link} className="inline-block mt-2 text-[12.5px] text-[#B0FC38] hover:underline">Open it in the portal →</Link>}
            </>
          ) : (
            <><div className="text-[14px] text-red-200 font-medium">The portal refused the write.</div><div className="mt-1 text-[12.5px] text-red-200/90">{outcome.error}</div><div className="mt-1 text-[12px] text-gray-400">The assistant will see this as failed. You can do it by hand from the school’s page.</div></>
          )}
        </section>
      )}

      {!pending && !outcome && (
        <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 text-[13px] text-gray-300" data-testid="action-closed">
          {action.status === 'denied' && <>Denied{action.decidedAt ? ` ${action.decidedAt}` : ''}.{action.note ? <> Note to the assistant: “{action.note}”</> : null}</>}
          {action.status === 'executed' && <>Done{action.executedAt ? ` ${action.executedAt}` : ''}.{action.result?.link && <> <Link to={action.result.link} className="text-[#B0FC38] hover:underline">Open it →</Link></>}</>}
          {action.status === 'failed' && <>Failed: {action.error}</>}
          {action.status === 'expired' && <>This proposal expired before anyone decided. Ask the assistant again.</>}
          {action.status === 'approved' && <>Approved. If this page was closed before the write finished, do it by hand from the school’s page.</>}
        </section>
      )}

      {action.tool === 'update_invoice' && editTarget && (
        <NewInvoiceDialog
          open={invoiceOpen}
          mode="edit"
          editTarget={editTarget}
          onClose={() => { setInvoiceOpen(false); if (action.status === 'approved' && !outcome) finish(false, null, 'The invoice editor was closed without saving'); }}
          onSaved={(saved) => { setInvoiceOpen(false); finish(true, { invoiceId: action.payload?.invoiceId, amountDollars: saved?.amountCents != null ? saved.amountCents / 100 : undefined, link: `/admin/invoices/${action.payload?.invoiceId}` }, null); }}
        />
      )}
      {action.tool === 'draft_invoice' && (
        <NewInvoiceDialog
          open={invoiceOpen}
          defaultAccountLocked
          onClose={() => { setInvoiceOpen(false); if (action.status === 'approved' && !outcome) finish(false, null, 'The invoice dialog was closed without saving'); }}
          prefill={{
            account: { id: action.payload?.accountId, name: action.accountName, type: 'school' },
            projectId: action.payload?.projectId || undefined,
            ...(action.payload?.amountDollars != null || action.payload?.description ? { lineItems: [{ description: action.payload?.description || 'Website', qty: 1, unitCostDollars: Number(action.payload?.amountDollars) || 0, product_type: null }] } : {}),
            notesInternal: `Drafted from an assistant proposal (${action.id})`,
          }}
          onCreated={(invoice) => { setInvoiceOpen(false); finish(true, { invoiceId: invoice?.id || null, link: invoice?.id ? `/admin/invoices/${invoice.id}` : `/admin/accounts/${action.payload?.accountId}?tab=financial` }, null); }}
        />
      )}
    </div>
  );
}
